import { useState } from 'react';
import type { AssignedProduct } from '@discovr/types';

import { cn } from '../../lib/utils';
import { Link } from '../../icons';

export interface LinkedProductsBarProps {
  /** The creator's assigned products (resolved — id + name), from the
   *  script/content detail's `assigned_products`. */
  products?: AssignedProduct[];
  /** Total products in the campaign catalog. Multi-Product Campaigns only
   *  activate at >= 2 campaign products; below that this renders nothing, so
   *  0-1 product campaigns look and behave exactly as before. Omit to skip the
   *  gate (caller has already gated). */
  campaignProductCount?: number;
  /** Chips shown before the rest collapse behind "N+ more". Default 4. */
  collapsedCount?: number;
  className?: string;
}

/**
 * LinkedProductsBar — the read-only "Link Products" row shown between the tab
 * bar and the tab content in the script- and content-review drawers
 * (Multi-Product Campaigns; Figma 2890:55564 / 2887:60252). Displays the
 * creator's assigned products as pills; identical for brand and admin
 * (assignment is admin-owned — this is display only). Collapsed it shows the
 * first `collapsedCount` pills + an "N+ more" control; clicking it expands
 * into a wrapped list so the rest becomes visible. Renders nothing when the
 * campaign has < 2 products or the creator has no assignment (the global >= 2
 * render gate).
 */
export function LinkedProductsBar({
  products,
  campaignProductCount,
  collapsedCount = 4,
  className,
}: LinkedProductsBarProps) {
  const [expanded, setExpanded] = useState(false);

  // Global gate: below 2 campaign products the feature is dormant.
  if (campaignProductCount !== undefined && campaignProductCount < 2) return null;
  if (!products || products.length === 0) return null;

  const hasOverflow = products.length > collapsedCount;
  const shown = expanded || !hasOverflow ? products : products.slice(0, collapsedCount);
  const hiddenCount = products.length - collapsedCount;

  return (
    <div
      className={cn('flex w-full flex-col gap-1.5 px-4 py-3', className)}
      data-testid="linked-products-bar"
    >
      <span className="flex items-center gap-1.5 text-body-sm-medium text-foreground-ink">
        <Link size={16} />
        Link Products
      </span>

      <div
        className={cn(
          'flex items-center gap-[13px]',
          expanded ? 'flex-wrap' : 'overflow-hidden',
        )}
      >
        {shown.map((p) => (
          <span
            key={p.product_id}
            title={p.notes || p.name}
            className="inline-flex h-6 shrink-0 items-center rounded-lg bg-background-primary-light-hover px-3 text-[13px] font-medium tracking-[-0.1px] text-muted-foreground"
          >
            {p.name}
          </span>
        ))}

        {hasOverflow && !expanded ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-label={`Show ${hiddenCount} more products`}
            className="shrink-0 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {hiddenCount}+ more
          </button>
        ) : null}
      </div>
    </div>
  );
}
